/**
 * Data projections - Pure transformation functions for UI
 * Converts raw DB data into UI-friendly structures
 */

import type { Scenario, List, Item } from '../domain/models'

export interface ScenarioStats {
  totalItems: number
  completedItems: number
  completionPercentage: number
}

export interface ListForUI extends List {
  itemsSorted: Item[]
}

export interface ScenarioForUI extends Scenario {
  lists: ListForUI[]
  stats: ScenarioStats
}

/**
 * Calculate scenario statistics
 */
export function calculateScenarioStats(scenario: Scenario & { lists: List[] }): ScenarioStats {
  let totalItems = 0
  let completedItems = 0

  for (const list of scenario.lists) {
    for (const item of Object.values(list.items ?? {})) {
      totalItems++
      if (item.isCompleted) {
        completedItems++
      }
    }
  }

  return {
    totalItems,
    completedItems,
    completionPercentage: totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0
  }
}

/**
 * Sort scenarios by name (case-insensitive alphabetical)
 */
export function sortScenariosByName(scenarios: Scenario[]): Scenario[] {
  return [...scenarios]
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Filter out deleted scenarios
 */
export function filterActiveScenarios(scenarios: Scenario[]): Scenario[] {
  return scenarios.filter(s => !s.isDeleted)
}

/**
 * Sort items: incomplete first, then by name
 */
export function sortItems(items: Record<string, Item> | undefined): Item[] {
  if (!items) return []
  return Object.values(items).sort((a, b) => {
    if (a.isCompleted !== b.isCompleted) {
      return a.isCompleted ? 1 : -1
    }
    return a.name.localeCompare(b.name)
  })
}

/**
 * Transform raw scenario data for UI
 */
export function projectScenarioForUI(scenario: Scenario & { lists: List[] }): ScenarioForUI {
  const stats = calculateScenarioStats(scenario)
  const sortedLists = scenario.lists
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(list => ({
      ...list,
      itemsSorted: sortItems(list.items)  // Provide sorted array for UI convenience
    }))
  
  return {
    ...scenario,
    lists: sortedLists,
    stats
  }
}

/**
 * Transform scenarios list for UI
 */
export function projectScenariosForUI(scenarios: Scenario[]) {
  const active = filterActiveScenarios(scenarios)
  return sortScenariosByName(active)
}
